import { Routes, Route, Navigate } from 'react-router-dom';
import { useEffect } from 'react';
import { useAuthStore } from './store/auth';

// Layouts
import MainLayout from './layouts/MainLayout';
import AuthLayout from './layouts/AuthLayout';
import DashboardLayout from './layouts/DashboardLayout';

// Public pages
import HomePage from './pages/HomePage';
import CoursesPage from './pages/CoursesPage';
import CourseDetailPage from './pages/CourseDetailPage';
import PricingPage from './pages/PricingPage';
import BecomeInstructorPage from './pages/BecomeInstructorPage';
import HelpCenterPage from './pages/HelpCenterPage';
import ContactPage from './pages/ContactPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import SupportPage from './pages/SupportPage';

// Auth pages
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import VerifyEmailPage from './pages/auth/VerifyEmailPage';

// Student pages
import StudentDashboard from './pages/student/Dashboard';
import MyCoursesPage from './pages/student/MyCoursesPage';
import CoursePlayerPage from './pages/student/CoursePlayerPage';

// Professor pages
import ProfessorDashboard from './pages/professor/Dashboard';
import ProfessorCoursesPage from './pages/professor/CoursesPage';
import CreateCoursePage from './pages/professor/CreateCoursePage';
import EditCoursePage from './pages/professor/EditCoursePage';
import ProfessorAnalyticsPage from './pages/professor/AnalyticsPage';
import ProfessorMeetingsPage from './pages/professor/MeetingsPage';
import CourseDocumentsPage from './pages/professor/CourseDocumentsPage';
import ProfessorEarningsPage from './pages/professor/EarningsPage';
import ProfessorVerificationPage from './pages/professor/VerificationPage';

// Shared pages
import SettingsPage from './pages/settings/SettingsPage';
import NotificationsPage from './pages/NotificationsPage';
import MeetingRoomPage from './pages/MeetingRoomPage';

// Protected route wrapper
import ProtectedRoute from './components/ProtectedRoute';
import { PageLoader } from './components/ui/PremiumLoader';

function App() {
  const { initAuth, isLoading } = useAuthStore();

  useEffect(() => {
    initAuth();
  }, [initAuth]);

  if (isLoading) {
    return <PageLoader text="Loading Ulearning..." />;
  }

  return (
    <Routes>
      {/* Public routes with main layout */}
      <Route element={<MainLayout />}>
        <Route path="/" element={<HomePage />} />
        <Route path="/courses" element={<CoursesPage />} />
        <Route path="/courses/:slug" element={<CourseDetailPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/become-instructor" element={<BecomeInstructorPage />} />
        <Route path="/help" element={<HelpCenterPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/terms" element={<TermsOfServicePage />} />
      </Route>

      {/* Support page - requires authentication */}
      <Route
        path="/support"
        element={
          <ProtectedRoute allowedRoles={['STUDENT', 'PROFESSOR']}>
            <MainLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<SupportPage />} />
      </Route>

      {/* Auth routes */}
      <Route element={<AuthLayout />}>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
      </Route>

      {/* Student routes */}
      <Route
        path="/student"
        element={
          <ProtectedRoute allowedRoles={['STUDENT']}>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<StudentDashboard />} />
        <Route path="my-courses" element={<MyCoursesPage />} />
        <Route path="courses" element={<CoursesPage />} />
        <Route path="course/:enrollmentId" element={<CoursePlayerPage />} />
        <Route path="notifications" element={<NotificationsPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="support" element={<SupportPage />} />
      </Route>

      {/* Professor routes */}
      <Route
        path="/professor"
        element={
          <ProtectedRoute allowedRoles={['PROFESSOR']}>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<ProfessorDashboard />} />
        <Route path="courses" element={<ProfessorCoursesPage />} />
        <Route path="courses/new" element={<CreateCoursePage />} />
        <Route path="courses/:id/edit" element={<EditCoursePage />} />
        <Route path="courses/:courseId/documents" element={<CourseDocumentsPage />} />
        <Route path="earnings" element={<ProfessorEarningsPage />} />
        <Route path="analytics" element={<ProfessorAnalyticsPage />} />
        <Route path="meetings" element={<ProfessorMeetingsPage />} />
        <Route path="notifications" element={<NotificationsPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="support" element={<SupportPage />} />
        <Route path="verification" element={<ProfessorVerificationPage />} />
      </Route>

      {/* Meeting room - accessible by both students and professors */}
      <Route
        path="/meeting/:meetingId"
        element={
          <ProtectedRoute allowedRoles={['STUDENT', 'PROFESSOR']}>
            <MeetingRoomPage />
          </ProtectedRoute>
        }
      />

      {/* Redirect based on role after login */}
      <Route path="/dashboard" element={<DashboardRedirect />} />

      {/* 404 */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// Redirect to appropriate dashboard based on role
function DashboardRedirect() {
  const { user, isAuthenticated, pendingVerification } = useAuthStore();

  // SECURITY: Redirect to verification if email not verified
  if (pendingVerification) {
    return <Navigate to="/verify-email" replace />;
  }

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />;
  }

  // SECURITY: Double-check email verification
  if (!user.emailVerified) {
    return <Navigate to="/verify-email" replace />;
  }

  if (user.role === 'PROFESSOR') {
    return <Navigate to="/professor" replace />;
  }

  return <Navigate to="/student" replace />;
}

export default App;

